import React from 'react';
import { Text } from 'wix-ui-tpa/cssVars';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { MenuItem } from '../../../../types/menu';
import type { ResolvedItemAvailability } from '../../../../utils/menuAvailability';
import {
  FeaturedBadgePlacement,
  getFeaturedBadgeAlignment,
} from '../../../../components-shared/HighlightedItem/constants';
import { featuredBadgeStylesParams } from '../../../../components-shared/HighlightedItem/stylesParams';
import { getItemFeaturedType } from '../../../../utils/featuredType';
import settingsParams from '../../settingsParams';
import { FeaturedBadge } from '../../../../components-shared/FeaturedBadge';
import type { QuickAddControlContext } from '../../actions/quickAddToCart';

import { classes, st } from './DishItem.st.css';
import { dishItemDataHooks } from './constants';
import { useHooks } from './useHooks';
import { LabelIcon } from './LabelIcon';
import { AddToCartControl } from './AddToCartControl';

type DishItemProps = {
  item: MenuItem;
  menuId: string;
  sectionId: string;
  sectionName?: string;
  currentSectionNumber?: number;
  availability?: ResolvedItemAvailability;
  onClick: (
    item: MenuItem,
    menuId: string,
    sectionId: string,
    availability?: ResolvedItemAvailability,
  ) => void;
  onQuickAdd: (item: MenuItem, context: QuickAddControlContext) => void;
  onQuickRemove: (item: MenuItem, context: QuickAddControlContext) => void;
  cartQuantity?: number;
  canAcceptOrders?: boolean;
  discountLabel?: string;
  discountedPrice?: string;
  className?: string;
};

const DishItem: React.FC<DishItemProps> = ({
  item,
  menuId,
  sectionId,
  sectionName,
  currentSectionNumber,
  availability,
  onClick,
  onQuickAdd,
  onQuickRemove,
  cartQuantity = 0,
  canAcceptOrders = true,
  discountLabel,
  discountedPrice,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const settings = useSettings();
  const badgePlacement = styles.get(
    featuredBadgeStylesParams.featuredBadgePlacement,
  );
  const badgeAlignment = getFeaturedBadgeAlignment(
    styles.get(featuredBadgeStylesParams.featuredBadgeAlignment),
  );

  const {
    layoutSize,
    isMobileLayoutSize,
    isAvailable,
    isCardLayout,
    isImageLeft,
    imageRatioName,
    imageResizeFit,
    shownLabels,
    extraLabelCount,
    shouldShowImage,
    shouldShowDescription,
    shouldShowLabels,
    shouldShowFeatured,
    shouldShowAddToCartControl,
    isAddToCartOnImage,
    shouldInlineAddToCartWithPrice,
    handleClick,
    handleKeyDown,
    priceText,
    imageUrl,
    imageAlt,
  } = useHooks({
    item,
    menuId,
    sectionId,
    availability,
    onClick,
    cartQuantity,
    canAcceptOrders,
  });

  const featuredType = getItemFeaturedType(item);
  const showFeaturedBadge = shouldShowFeatured && Boolean(featuredType);
  const shouldApplyHighlightedItemStyles =
    showFeaturedBadge && settings.get(settingsParams.overrideItemDesign);
  const isBadgeOnImage =
    showFeaturedBadge &&
    isCardLayout &&
    shouldShowImage &&
    badgePlacement === FeaturedBadgePlacement.OnImage;
  const showBadgeInItemInfo = showFeaturedBadge && !isBadgeOnImage;

  const priceAndDiscount = (
    <div className={classes.priceAndDiscount}>
      {discountedPrice ? (
        <div className={classes.discountedPriceRow}>
          <Text
            className={classes.originalPrice}
            data-hook={dishItemDataHooks.price()}
            tagName="span"
          >
            {priceText}
          </Text>
          <Text className={classes.price} tagName="span">
            {discountedPrice}
          </Text>
        </div>
      ) : (
        <Text
          className={classes.price}
          data-hook={dishItemDataHooks.price()}
          tagName="p"
        >
          {priceText}
        </Text>
      )}
      {discountLabel && (
        <span
          className={classes.discountLabel}
          data-hook={dishItemDataHooks.discountLabel()}
        >
          {discountLabel}
        </span>
      )}
    </div>
  );

  const renderAddToCartControl = (inline: boolean) =>
    shouldShowAddToCartControl &&
    (!isAddToCartOnImage || !shouldShowImage) && (
      <AddToCartControl
        item={item}
        menuId={menuId}
        sectionId={sectionId}
        sectionName={sectionName}
        currentSectionNumber={currentSectionNumber}
        cartQuantity={cartQuantity}
        isAvailable={isAvailable}
        canAcceptOrders={canAcceptOrders}
        onQuickAdd={onQuickAdd}
        onQuickRemove={onQuickRemove}
        placement="itemInfo"
        inline={inline}
        // Own-row case needs explicit spacing from .priceBlock above it —
        // doesn't rely on the parent's flex gap, since this can render
        // through the display:contents .desktopAddToCartRow wrapper.
        className={inline ? undefined : classes.ownRowAddToCart}
      />
    );

  // On a narrow strip there's no room next to the image, so the price/button
  // row moves below everything instead of sitting beside the image (mirrors
  // MenuLayout's own mobile handling) — Card layout never needs this, it's
  // already full width at any breakpoint.
  const showMobileAddToCartRow = isMobileLayoutSize && !isCardLayout;

  const addToCartRow = (
    <>
      <div className={classes.priceBlock}>
        <div className={classes.priceRow}>
          {priceAndDiscount}
          {shouldInlineAddToCartWithPrice && renderAddToCartControl(true)}
        </div>
      </div>
      {!shouldInlineAddToCartWithPrice && renderAddToCartControl(false)}
    </>
  );

  return (
    <div
      className={st(
        classes.root,
        {
          [layoutSize]: true,
          unavailable: !isAvailable,
          card: isCardLayout,
          imageLeft: isImageLeft,
          imageRatio: imageRatioName,
          resizeFit: imageResizeFit,
          highlighted: shouldApplyHighlightedItemStyles,
        },
        className,
      )}
      data-hook={dishItemDataHooks.root()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-haspopup="true"
    >
      <div className={classes.contentRow}>
        <div className={classes.textContent}>
          <div className={classes.topContent}>
            {showBadgeInItemInfo && (
              <FeaturedBadge
                dataHook={dishItemDataHooks.featuredBadge()}
                featuredType={featuredType}
              />
            )}
            <div className={classes.titleRow}>
              {cartQuantity > 0 && (
                <span
                  className={classes.cartBadge}
                  data-hook={dishItemDataHooks.cartBadge()}
                >
                  {cartQuantity}
                </span>
              )}
              <Text
                className={classes.title}
                data-hook={dishItemDataHooks.title()}
                tagName="p"
              >
                {item.name}
              </Text>
            </div>
            <div className={classes.descriptionBlock}>
              {shouldShowDescription && (
                <Text
                  className={classes.description}
                  data-hook={dishItemDataHooks.description()}
                  tagName="p"
                >
                  {item.description}
                </Text>
              )}
              {shouldShowLabels && (
                <div
                  className={classes.labelsRow}
                  data-hook={dishItemDataHooks.labels()}
                >
                  {shownLabels.map((label) => (
                    <LabelIcon
                      key={label.id}
                      className={classes.labelIcon}
                      url={label.icon.url ?? ''}
                      alt={label.name}
                    />
                  ))}
                  {extraLabelCount > 0 && (
                    <Text
                      className={classes.labelCounter}
                      data-hook={dishItemDataHooks.labelCounter()}
                      tagName="span"
                    >
                      +{extraLabelCount}
                    </Text>
                  )}
                </div>
              )}
            </div>
          </div>
          {!showMobileAddToCartRow && (
            <div className={classes.desktopAddToCartRow}>{addToCartRow}</div>
          )}
        </div>
        {shouldShowImage && (
          <div className={classes.imageWrapper}>
            <img
              className={classes.image}
              src={imageUrl}
              alt={imageAlt}
              data-hook={dishItemDataHooks.image()}
              loading="lazy"
            />
            {shouldShowAddToCartControl && isAddToCartOnImage && (
              <AddToCartControl
                item={item}
                menuId={menuId}
                sectionId={sectionId}
                sectionName={sectionName}
                currentSectionNumber={currentSectionNumber}
                cartQuantity={cartQuantity}
                isAvailable={isAvailable}
                canAcceptOrders={canAcceptOrders}
                onQuickAdd={onQuickAdd}
                onQuickRemove={onQuickRemove}
                placement="onImage"
              />
            )}
            {isBadgeOnImage && featuredType && (
              <FeaturedBadge
                dataHook={dishItemDataHooks.featuredBadge()}
                featuredType={featuredType}
                onImage
                alignment={badgeAlignment}
              />
            )}
          </div>
        )}
      </div>

      {showMobileAddToCartRow && (
        <div className={classes.mobileAddToCartRow}>{addToCartRow}</div>
      )}
    </div>
  );
};

export default DishItem;
